/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Table, Form } from 'react-bootstrap'
import { Card } from 'react-bootstrap-v5'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { numberFormat } from '../helpers/GlobalHelper'
import GetPagePermission from '../components/GetPagePermission'

type ItemType = 'raw_material' | 'finished_product'

interface SummaryRow {
	id: number
	name: string
	code: string
	packing: string
	size: string
	categoryName: string
	unitName: string
	opening_qty: number
	received_qty: number
	consumption_qty: number
	closing_qty: number
}

const ALL_WAREHOUSES = { label: 'All Warehouses', value: '' }

const toInputDate = (date: Date) => {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

const stickyHeaderStyle: React.CSSProperties = {
	position: 'sticky',
	top: 0,
	zIndex: 1,
	backgroundColor: '#f8f9fa',
	whiteSpace: 'nowrap',
}

const qtyCell = (value: number) => (
	<td className={`text-end${value < 0 ? ' text-danger' : ''}`}>{numberFormat(value)}</td>
)

const StockSummaryReport: React.FC = () => {
	const hasPermission = GetPagePermission('/stock-summary-report')

	const today = new Date()
	const [projects, setProjects] = useState<any[]>([])
	const [project, setProject] = useState<any>(null)
	const [warehouses, setWarehouses] = useState<any[]>([])
	const [warehouse, setWarehouse] = useState<any>(ALL_WAREHOUSES)
	const [fromDate, setFromDate] = useState(toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)))
	const [toDate, setToDate] = useState(toInputDate(today))
	const [itemType, setItemType] = useState<ItemType>('raw_material')
	const [search, setSearch] = useState('')

	const [rows, setRows] = useState<SummaryRow[]>([])
	const [loading, setLoading] = useState(false)
	const requestId = useRef(0)

	const projectValue = project?.value ? String(project.value) : ''
	const warehouseValue = warehouse?.value ? String(warehouse.value) : ''
	const isFinishedProduct = itemType === 'finished_product'

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options = (resp.data || []).map((row: any) => ({ label: row.name, value: row.id }))
				setProjects(options)
				if (options.length) setProject(options[0])
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}, [])

	useEffect(() => {
		if (!projectValue) {
			setWarehouses([])
			return
		}
		fetch(process.env.REACT_APP_API_URL + 'warehouse/' + projectValue + '/list', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setWarehouses(resp.success && Array.isArray(resp.data)
					? resp.data.map((row: any) => ({ label: row.name, value: String(row.id_warehouses) }))
					: [])
			})
			.catch(() => setWarehouses([]))
	}, [projectValue])

	const filterParams = {
		id_projects: projectValue,
		id_warehouses: warehouseValue,
		from_date: fromDate,
		to_date: toDate,
		item_type: itemType,
	}

	// Reload whenever a filter changes; ignore responses from superseded requests
	useEffect(() => {
		if (!projectValue || !toDate || (fromDate && fromDate > toDate)) {
			setRows([])
			return
		}
		const currentRequest = ++requestId.current
		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'reports/stock-summary', postRequestOptions(filterParams))
			.then((resp) => resp.json())
			.then((resp) => {
				if (currentRequest !== requestId.current) return
				if (resp.success) {
					setRows(resp.data || [])
				} else {
					setRows([])
					;(resp.errors || ['Failed to load report']).forEach((error: string) =>
						toast.error(error, { position: 'bottom-right', autoClose: 4000 }))
				}
			})
			.catch(() => {
				if (currentRequest === requestId.current) {
					setRows([])
					toast.error('Error loading report', { position: 'bottom-right', autoClose: 4000 })
				}
			})
			.finally(() => {
				if (currentRequest === requestId.current) setLoading(false)
			})
	}, [projectValue, warehouseValue, fromDate, toDate, itemType])

	const filteredRows = useMemo(() => {
		const term = search.trim().toLowerCase()
		if (!term) return rows
		return rows.filter((row) =>
			[row.name, row.code, row.categoryName, row.packing].some((value) => (value || '').toLowerCase().includes(term)))
	}, [rows, search])

	const detailsLink = (row: SummaryRow) => {
		const params = new URLSearchParams({ id_projects: projectValue })
		if (warehouseValue) params.set('id_warehouses', warehouseValue)
		return isFinishedProduct
			? `/finished-goods-stock-details?id_products=${row.id}&${params.toString()}`
			: `/item-stock-details?id_items=${row.id}&${params.toString()}`
	}

	const columnCount = isFinishedProduct ? 11 : 9

	const renderTable = () => (
		<div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
			<Table bordered hover size='sm' className='mb-0'>
				<thead>
					<tr>
						<th className='text-center' style={stickyHeaderStyle}>SL</th>
						<th style={stickyHeaderStyle}>{isFinishedProduct ? 'Product Name' : 'Item Name'}</th>
						<th style={stickyHeaderStyle}>Code</th>
						{isFinishedProduct && <th style={stickyHeaderStyle}>Packing</th>}
						{isFinishedProduct && <th style={stickyHeaderStyle}>Size</th>}
						<th style={stickyHeaderStyle}>Category</th>
						<th style={stickyHeaderStyle}>Unit</th>
						<th className='text-end' style={stickyHeaderStyle}>Opening Qty</th>
						<th className='text-end' style={stickyHeaderStyle}>Received Qty</th>
						<th className='text-end' style={stickyHeaderStyle}>Consumption Qty</th>
						<th className='text-end' style={stickyHeaderStyle}>Closing Qty</th>
					</tr>
				</thead>
				<tbody>
					{loading ? (
						<tr>
							<td colSpan={columnCount} className='text-center py-4'>
								<span className='spinner-border spinner-border-sm me-2'></span>Loading...
							</td>
						</tr>
					) : filteredRows.length === 0 ? (
						<tr>
							<td colSpan={columnCount} className='text-center text-muted py-4'>
								{projectValue ? 'No stock movement found for the selected filters.' : 'Please select a project.'}
							</td>
						</tr>
					) : (
						filteredRows.map((row, index) => (
							<tr key={row.id}>
								<td className='text-center'>{index + 1}</td>
								<td><Link to={detailsLink(row)}>{row.name}</Link></td>
								<td>{row.code || '-'}</td>
								{isFinishedProduct && <td>{row.packing || '-'}</td>}
								{isFinishedProduct && <td>{row.size || '-'}</td>}
								<td>{row.categoryName || '-'}</td>
								<td>{row.unitName || '-'}</td>
								{qtyCell(row.opening_qty)}
								{qtyCell(row.received_qty)}
								{qtyCell(row.consumption_qty)}
								{qtyCell(row.closing_qty)}
							</tr>
						))
					)}
				</tbody>
			</Table>
		</div>
	)

	if (hasPermission !== 1) {
		return null
	}

	return (
		<Card className='mb-4'>
			<Card.Body>
				<Card.Title className='h5 mb-1'>Stock Summary</Card.Title>
				<p className='text-muted small mb-3'>
					Opening stock before the From Date, quantity received and consumed in the period, and closing stock as on the selected date.
					{!warehouseValue && ' With All Warehouses, transfers between warehouses are not counted as received or consumed.'}
				</p>
				<div className='row align-items-end'>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='project'>
							<Form.Label>Project<span className='required'></span></Form.Label>
							<Select
								value={project}
								onChange={(selected: any) => {
									setProject(selected)
									setWarehouse(ALL_WAREHOUSES)
								}}
								options={projects}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								placeholder='Select Project'
							/>
						</Form.Group>
					</div>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='warehouse'>
							<Form.Label>Warehouse</Form.Label>
							<Select
								value={warehouse}
								onChange={(selected: any) => setWarehouse(selected || ALL_WAREHOUSES)}
								options={[ALL_WAREHOUSES, ...warehouses]}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								isDisabled={!projectValue}
							/>
						</Form.Group>
					</div>
					<div className='col-md-2'>
						<Form.Group className='mb-3' controlId='fromDate'>
							<Form.Label>From Date</Form.Label>
							<Form.Control type='date' value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} />
						</Form.Group>
					</div>
					<div className='col-md-2'>
						<Form.Group className='mb-3' controlId='toDate'>
							<Form.Label>As On Date<span className='required'></span></Form.Label>
							<Form.Control type='date' value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
						</Form.Group>
					</div>
					<div className='col-md-2 mb-3'>
						<a
							className={`btn btn-sm btn-primary w-100 py-3 text-nowrap${projectValue ? '' : ' disabled'}`}
							href={projectValue ? `${process.env.REACT_APP_BACKEND}stock_summary_report_download?${new URLSearchParams(filterParams).toString()}` : undefined}
						>
							<i className='fas fa-download me-1'></i>Excel Download
						</a>
					</div>
				</div>

				<div className='d-flex justify-content-between align-items-center mb-2'>
					<span className='text-muted small'>
						{loading ? '' : `${filteredRows.length} of ${rows.length} ${isFinishedProduct ? 'products' : 'items'}`}
					</span>
					<div style={{ minWidth: '260px' }}>
						<Form.Control
							type='text'
							size='sm'
							placeholder='Search name, code or category...'
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>

				<Tabs
					activeKey={itemType}
					onSelect={(key) => setItemType((key as ItemType) || 'raw_material')}
					id='stock-summary-tabs'
					className='mb-3'
				>
					<Tab eventKey='raw_material' title='Raw Material'>
						{itemType === 'raw_material' && renderTable()}
					</Tab>
					<Tab eventKey='finished_product' title='Finished Product'>
						{itemType === 'finished_product' && renderTable()}
					</Tab>
				</Tabs>
			</Card.Body>
		</Card>
	)
}

export default StockSummaryReport
